import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/** Bordered card with a title row. */
export function Section({
  title,
  actions,
  children,
  className,
  bodyClassName,
  id,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  id?: string;
}) {
  return (
    <section aria-labelledby={id} className={cn('rounded-lg border border-border bg-panel', className)}>
      <header className="flex min-h-10 items-center gap-2 border-b border-border px-3.5">
        <h2 id={id} className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
          {title}
        </h2>
        {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
      </header>
      <div className={cn('p-3.5', bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-sm text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

const TONES = {
  neutral: 'border-border-strong bg-panel-2 text-fg-2',
  accent: 'border-accent/40 bg-accent/12 text-accent',
  success: 'border-success/30 bg-success/10 text-success',
  warn: 'border-warn/30 bg-warn/10 text-warn',
  danger: 'border-danger/30 bg-danger/10 text-danger',
  info: 'border-info/30 bg-info/10 text-info',
  claude: 'border-claude/30 bg-claude/10 text-claude',
} as const;
export type Tone = keyof typeof TONES;

/** 11px pill badge: 2px 6px padding, 4px radius, tinted. */
export function Badge({
  tone = 'neutral',
  className,
  ...p
}: HTMLAttributes<HTMLSpanElement> & { tone?: Tone }) {
  return (
    <span
      {...p}
      className={cn(
        'inline-flex items-center gap-1 rounded-xs border px-1.5 py-0.5 text-[11px] leading-none font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    />
  );
}

export function CountBadge({
  n,
  tone = 'neutral',
}: {
  n: number | undefined;
  tone?: 'neutral' | 'warn' | 'accent';
}) {
  if (!n) return null;
  return (
    <span
      className={cn(
        'ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-md px-1.5 font-mono text-[10.5px] font-medium',
        tone === 'warn'
          ? 'bg-warn/15 text-warn'
          : tone === 'accent'
            ? 'bg-accent text-accent-fg'
            : 'bg-hover text-fg-2',
      )}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-[18px] items-center rounded-xs border border-border-strong bg-panel-2 px-1 font-mono text-2xs text-muted">
      {children}
    </kbd>
  );
}

export function Empty({ children, icon = '∅' }: { children: ReactNode; icon?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 py-10 text-center text-sm text-muted">
      <span aria-hidden className="font-mono text-lg text-faint">
        {icon}
      </span>
      {children}
    </div>
  );
}

/** API codes of a plan limit or a higher-tier feature: the box links to the subscription page. */
const PLAN_CODES = new Set(['plan_limit', 'plan_feature']);

export function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = (error as { code?: unknown }).code;
  return (
    <div
      role="alert"
      className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
    >
      {(error instanceof Error ? error.message : String(error)) || 'Something went wrong'}
      {code === '2fa_required' && (
        <>
          {' '}
          <a href="/settings/security" className="font-medium underline">
            {t('billing.setup2fa')}
          </a>
        </>
      )}
      {typeof code === 'string' && PLAN_CODES.has(code) && (
        <>
          {' '}
          <a href="/billing" className="font-medium underline">
            {t('billing.upgrade')}
          </a>
        </>
      )}
    </div>
  );
}

export function Banner({
  tone = 'warn',
  children,
}: {
  tone?: 'warn' | 'danger' | 'info';
  children: ReactNode;
}) {
  const cls = {
    warn: 'border-warn/30 bg-warn/10 text-warn',
    danger: 'border-danger/40 bg-danger/10 text-danger',
    info: 'border-accent/30 bg-accent/10 text-info',
  }[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('rounded-md border px-3 py-2 text-sm', cls)}
    >
      {children}
    </div>
  );
}

/** Dense key/value grid. */
export function KV({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
      {items.map(([k, v], i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static list
        <div key={i} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="min-w-0 truncate">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** KPI tile: small muted label, big Inter number, optional small unit + sub line. */
export function Stat({
  label,
  value,
  unit,
  sub,
  tone,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <div className={cn('rounded-lg border border-border bg-panel px-3.5 py-3', className)}>
      <div className="text-xs text-muted">{label}</div>
      <div
        className={cn(
          'mt-1 text-2xl leading-none font-semibold tracking-tight tabular-nums',
          tone === 'danger' && 'text-danger',
          tone === 'success' && 'text-success',
          tone === 'warn' && 'text-warn',
        )}
      >
        {value}
        {unit ? <span className="ml-0.5 text-xs font-normal text-muted">{unit}</span> : null}
      </div>
      {sub ? <div className="mt-1 text-xs text-faint tabular-nums">{sub}</div> : null}
    </div>
  );
}

/** 3-up KPI group with shared borders (reference: "Checks / Build / Coverage"). */
export function StatGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'grid grid-cols-3 divide-x divide-border overflow-hidden rounded-lg border border-border bg-panel [&>*]:rounded-none [&>*]:border-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
